<template>
  <div class="users-page">
    <!-- =========================  HEADER  ========================= -->
    <div class="kyc-page-header mb-3 d-flex align-items-start justify-content-between flex-wrap">
      <div>
        <h2 class="kyc-page-title mb-1">KYC-панель</h2>
        <div class="kyc-page-subtitle">Верификация клиентов и управление досье</div>
      </div>
      <b-button variant="outline-secondary" size="sm" class="kyc-export-btn" @click="exportCsv">
        <b-icon icon="download" class="mr-1" /> Экспорт CSV
      </b-button>
    </div>

    <!-- =========================  STAT CARDS  ===================== -->
    <b-row class="kyc-stats-row mb-3" no-gutters>
      <b-col v-for="card in statCards" :key="card.key" class="kyc-stat-col">
        <div
          class="kyc-stat-card"
          :class="{ 'is-active': activeStatCard === card.key, ['kyc-stat--' + card.tone]: true }"
          @click="onStatCardClick(card)"
        >
          <div class="kyc-stat-icon">
            <b-icon :icon="card.icon" />
          </div>
          <div class="kyc-stat-body">
            <div class="kyc-stat-label">{{ card.label }}</div>
            <div class="kyc-stat-value" :class="'text-' + card.tone">{{ kycStatValue(card.key) }}</div>
            <div class="kyc-stat-hint">{{ card.hint }}</div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- =========================  TABS + FILTER TOGGLE  =========== -->
    <div class="kyc-tabs-row d-flex align-items-center justify-content-between mb-3 flex-wrap">
      <div class="kyc-tabs">
        <button
          v-for="t in entityTabs"
          :key="t.value"
          type="button"
          class="kyc-tab"
          :class="{ active: isOrg === t.value }"
          @click="setEntityTab(t.value)"
        >
          {{ t.label }}
          <span class="kyc-tab-count">{{ entityTabCount(t.value) }}</span>
        </button>
      </div>
      <b-button
        variant="outline-secondary"
        size="sm"
        class="kyc-filter-btn"
        :class="{ 'is-active': showAdvancedFilters }"
        @click="showAdvancedFilters = !showAdvancedFilters"
      >
        <b-icon icon="funnel" class="kyc-filter-btn-icon" />
        <span>Фильтр</span>
        <b-icon icon="chevron-down" class="kyc-filter-btn-chevron" />
      </b-button>
    </div>

    <!-- =========================  QUICK CHIPS  ==================== -->
    <div class="kyc-chips-row mb-3 d-flex align-items-center flex-wrap">
      <span class="kyc-chips-label mr-2">Быстро:</span>
      <button
        v-for="chip in quickChips"
        :key="chip.key"
        type="button"
        class="kyc-chip"
        :class="['kyc-chip--' + chip.tone, { active: activeChip === chip.key }]"
        @click="onChipClick(chip)"
      >
        <span class="kyc-chip-dot" />
        {{ chip.label }}
      </button>

      <div class="total-pill ml-auto">
        <b-icon icon="people" class="mr-1"></b-icon>
        <strong>Найдено:</strong>
        <span class="ml-1">{{ totalRows | money(0) }}</span>
      </div>
    </div>

    <!-- ====  ADVANCED FILTERS  (KYC_interactive mock; only working
         fields — Sumsub / Shufti / Business Status placeholders are gone
         until those columns actually exist on the backend) -->
    <b-collapse v-model="showAdvancedFilters" class="mb-3">
    <b-card class="kyc-filters-card" body-class="p-3">
      <b-row>
        <b-col md="3" sm="6" class="mb-2">
          <label class="kyc-filter-label">Тип аккаунта</label>
          <b-form-select v-model.number="isOrg" size="sm" :options="userTypeOptions" />
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <label class="kyc-filter-label">Статус аккаунта</label>
          <b-form-select v-model="filterAccountStatus" size="sm" :options="accountStatusOptions" />
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <label class="kyc-filter-label">Уровень риска</label>
          <b-form-select v-model="filterRiskLevel" size="sm" :options="riskLevelOptions" />
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <label class="kyc-filter-label">Статус классификации</label>
          <b-form-select v-model="filterClassification" size="sm" :options="classificationOptions" />
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <label class="kyc-filter-label">Гражданство</label>
          <b-form-select v-model="filterNationality" size="sm" :options="nationalityOptions" />
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <label class="kyc-filter-label">Резидентство</label>
          <b-form-select v-model="filterResidency" size="sm" :options="residencyOptions" />
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <label class="kyc-filter-label">Период регистрации</label>
          <b-form-select v-model="datePreset" size="sm" :options="datePresetOptions" @change="onDatePreset(datePreset)" />
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <label class="kyc-filter-label">Onboarding (raw)</label>
          <b-form-select v-model="filterOnboarding" size="sm" :options="onboardingOptions" />
        </b-col>
      </b-row>
      <hr class="kyc-filters-divider" />
      <b-row>
        <b-col md="12" class="mb-2">
          <label class="kyc-filter-label">Поиск</label>
          <b-form-input v-model="filter" type="search" size="sm" placeholder="Поиск по Email, ФИО, ИИН, логину..." />
        </b-col>
      </b-row>
      <div class="d-flex justify-content-end mt-2">
        <b-button size="sm" variant="outline-secondary" class="mr-2" @click="resetFilters">Сбросить фильтры</b-button>
        <b-button size="sm" variant="primary" @click="applyFilters">
          <b-icon icon="search" class="mr-1" />Найти
        </b-button>
      </div>
    </b-card>
    </b-collapse>

    <!-- Панель инструментов скрыта: «Новый» не нужен, «Общее число»
         перенесено в строку тегов выше. -->
    <b-card v-if="false" class="toolbar-card mb-3" body-class="py-3">
      <b-row class="align-items-center">
        <!-- Действия -->
        <b-col lg="6" class="mb-2 mb-lg-0">
          <div class="d-flex flex-wrap gap-8">
            <!-- Кнопка «Новый» скрыта: пользователи создаются через регистрацию.
            <b-button variant="success" size="sm" class="btn-soft" v-b-modal.userModal>
              <b-icon icon="plus-circle" class="mr-1"></b-icon>
              Новый
            </b-button>
            -->

            <b-button
              variant="info"
              size="sm"
              class="btn-soft"
              v-if="selectedRows.length === 1"
              @click="updateRecord"
            >
              <b-icon icon="pencil-square" class="mr-1"></b-icon>
              Редактировать
            </b-button>

            <b-button
              variant="danger"
              size="sm"
              class="btn-soft"
              v-if="selectedRows.length > 0"
              @click="deleteRecord"
            >
              <b-icon icon="trash" class="mr-1"></b-icon>
              Удалить
              <span class="ml-1 badge badge-light">{{ selectedRows.length }}</span>
            </b-button>
          </div>
        </b-col>

        <!-- Старая панель поиск/роль/тип удалена — её роль теперь
             выполняет блок KYC-фильтров выше. -->
      </b-row>

      <b-row class="mt-3">
        <b-col cols="12" class="text-right">
          <div class="total-pill">
            <b-icon icon="people" class="mr-1"></b-icon>
            <strong>Общее число:</strong>
            <span class="ml-1">{{ totalRows | money(0) }}</span>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- =========================  BULK BAR  ===================== -->
    <div v-if="selectedRowIds.length" class="kyc-bulk-bar mb-3 d-flex align-items-center justify-content-between">
      <div class="kyc-bulk-label">
        <strong>{{ selectedRowIds.length }} выбрано</strong>
      </div>
      <div class="d-flex align-items-center gap-1">
        <b-button size="sm" variant="outline-secondary" class="mr-2" @click="exportSelected">
          <b-icon icon="download" class="mr-1" /> Экспорт выбранных
        </b-button>
        <b-button size="sm" variant="danger" class="mr-2" @click="bulkBlockSelected">
          <b-icon icon="lock-fill" class="mr-1" /> Заблокировать
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="clearSelection">
          <b-icon icon="x" />
        </b-button>
      </div>
    </div>

    <!-- =========================  ТАБЛИЦА  ====================== -->
    <b-card class="table-card" body-class="p-0">
      <b-table
        ref="userTable"
        responsive
        :items="userProvider"
        :fields="fields"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :table-variant="tableVariant"
        :filter="filter"
        :tbody-tr-class="rowClassByRisk"
        @row-clicked="onRowClicked"
        class="mb-0 kyc-users-table"
      >
        <template v-slot:table-busy>
          <div class="text-center my-4">
            <b-spinner class="align-middle"></b-spinner>
            <div class="mt-2 text-muted">Загрузка...</div>
          </div>
        </template>

        <template v-slot:empty>
          <div class="text-center my-4 text-muted">
            <b-icon icon="inbox" font-scale="1.3"></b-icon>
            <div class="mt-2">Нет данных</div>
          </div>
        </template>

        <!-- Bulk-select checkbox per row. Click is stopped so the row
             doesn't also navigate when toggling selection. -->
        <template v-slot:head(select)>
          <b-form-checkbox
            :checked="allOnPageSelected"
            :indeterminate="someOnPageSelected"
            @change="toggleAllOnPage"
            @click.native.stop
          />
        </template>
        <template v-slot:cell(select)="row">
          <span @click.stop>
            <b-form-checkbox
              :checked="isSelected(row.item.id)"
              @change="toggleSelect(row.item.id)"
            />
          </span>
        </template>

        <template v-slot:cell(email)="row">
          <div class="kyc-cell-email">{{ row.item.email }}</div>
        </template>
        <template v-slot:cell(userName)="row">
          <span class="text-muted text-monospace small">{{ row.item.userName || '—' }}</span>
        </template>
        <template v-slot:cell(fullName)="row">
          <div class="font-weight-semibold">{{ fullName(row.item) }}</div>
        </template>
        <template v-slot:cell(iin)="row">
          <span class="text-muted text-monospace small">{{ row.item.iin || '—' }}</span>
        </template>
        <template v-slot:cell(userType)="row">
          {{ row.item.isOrg ? 'Юр. лицо' : 'Физ. лицо' }}
        </template>
        <template v-slot:cell(riskLevel)="row">
          <span :class="['kyc-risk-text', 'kyc-risk-text--' + riskLevel(row.item)]">
            {{ riskLabel(row.item) }}
          </span>
        </template>
        <template v-slot:cell(classification)="row">
          <span class="small kyc-cls-cell">
            <span>{{ classificationLabel(row.item.clientCategory) }}</span>
            <span
              v-if="row.item.hasActiveReclass"
              class="kyc-recls-dot"
              v-b-tooltip.hover
              title="Активная заявка на реклассификацию"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
            </span>
          </span>
        </template>
        <template v-slot:cell(limit)="row">
          <strong>{{ limitDisplay(row.item) }}</strong>
        </template>
        <template v-slot:cell(status)="row">
          <b-badge :variant="statusInfo(row.item).tone" pill class="kyc-status-pill">
            {{ statusInfo(row.item).label }}
          </b-badge>
        </template>
        <template v-slot:cell(actions)="row">
          <span class="d-flex gap-1" @click.stop>
            <b-button
              variant="outline-primary"
              size="sm"
              class="btn-round mr-1"
              @click="openUserCard(row.item)"
              v-b-tooltip.hover
              title="Открыть карточку"
            >
              <b-icon icon="box-arrow-up-right" />
            </b-button>
            <b-button
              :variant="row.item.isActive == 1 ? 'outline-success' : 'outline-danger'"
              size="sm"
              class="btn-round mr-1"
              @click="askBlock(row.item)"
              v-b-tooltip.hover
              :title="row.item.isActive == 1 ? 'Заблокировать' : 'Разблокировать'"
            >
              <b-icon :icon="row.item.isActive == 1 ? 'unlock-fill' : 'lock-fill'" />
            </b-button>
            <b-button
              :variant="isRowFrozen(row.item) ? 'outline-info' : 'outline-warning'"
              size="sm"
              class="btn-round"
              @click="askFreeze(row.item)"
              v-b-tooltip.hover
              :title="isRowFrozen(row.item) ? 'Разморозить аккаунт' : 'Заморозить аккаунт'"
            >
              <b-icon :icon="isRowFrozen(row.item) ? 'droplet' : 'snow'" />
            </b-button>
          </span>
        </template>
      </b-table>

      <div class="p-3 border-top">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="center"
          size="sm"
          class="mb-0"
        />
      </div>
    </b-card>

    <!-- Модальное окно: Пользователь -->
    <b-modal id="userModal" title="Пользователь" hide-footer @hidden="modalHidden">
      <b-form v-on:submit.prevent="submitUser">
        <b-form-row class="mb-3">
          <b-col md="12" class="mb-2">
            <label for="roleId">Роль</label>
            <select class="form-control form-control-sm text-center" id="roleId" v-model="userForm.roleId">
              <option :value="0">Выберите</option>
              <option v-for="(r,ri) in roles" :key="ri" :value="r.id">
                {{ r.name }}
              </option>
            </select>
          </b-col>

          <b-col md="12" class="mb-2">
            <label for="lastName">Фамилия</label>
            <b-form-input id="lastName" v-model="userForm.lastName" required size="sm" />
          </b-col>

          <b-col md="12" class="mb-2">
            <label for="firstName">Имя</label>
            <b-form-input id="firstName" v-model="userForm.firstName" required size="sm" />
          </b-col>

          <b-col md="12" class="mb-2">
            <label for="thirdName">Отчество</label>
            <b-form-input id="thirdName" v-model="userForm.thirdName" required size="sm" />
          </b-col>

          <b-col md="12" class="mb-2">
            <label for="userEmail">Email</label>
            <b-form-input id="userEmail" v-model="userForm.email" type="email" required size="sm" />
          </b-col>

          <b-col md="12" class="mb-2">
            <label for="userPhone">Телефон</label>
            <b-form-input id="userPhone" v-model="userForm.phoneNumber" required size="sm" placeholder="Телефон" />
          </b-col>

          <b-col md="12" class="mb-2">
            <label for="password">Пароль</label>
            <b-form-input id="password" v-model="userForm.password" type="password" required size="sm" />
          </b-col>
        </b-form-row>

        <div class="d-flex justify-content-end">
          <b-button type="reset" variant="outline-secondary" class="mr-2">
            <b-icon icon="arrow-counterclockwise" class="mr-1"></b-icon>
            Очистить
          </b-button>
          <b-button type="submit" variant="primary" class="btn-soft">
            <b-icon icon="check2" class="mr-1"></b-icon>
            Сохранить
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Модальное окно: Документы пользователя (квалификация / профессиональные) -->
    <b-modal
      id="docsModal"
      :title="docsModalTitle"
      size="lg"
      modal-class="docs-modal"
      header-class="docs-modal-header"
      body-class="docs-modal-body"
      hide-footer
      @hidden="docsModalHidden"
    >
      <div v-if="docsLoading" class="docs-loading">
        <b-spinner variant="primary"></b-spinner>
        <span class="ml-2">Загрузка документов...</span>
      </div>
      <div v-else class="docs-content">
        <div v-if="docsError" class="docs-error alert alert-warning">
          <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
          {{ docsError }}
        </div>
        <div v-else class="docs-list-wrap">
          <div v-if="!docsList.length" class="docs-empty">
            <b-icon icon="folder2-open" font-scale="2" class="text-muted"></b-icon>
            <p class="mt-2 mb-0 text-muted">Нет документов</p>
          </div>
          <div v-else class="docs-list">
            <div
              v-for="(doc, index) in docsList"
              :key="doc.docId || index"
              class="doc-card"
            >
              <div class="doc-card-header">
                <span class="doc-id">#{{ doc.docId }}</span>
                <span class="doc-type">{{ docTypeLabel(doc.docType) }}</span>
                <b-badge :variant="docStatusVariant(doc.docStatus)" class="doc-status">
                  {{ doc.docStatus }}
                </b-badge>
              </div>
              <div class="doc-card-body">
                <div class="doc-path text-muted small mb-2">
                  {{ docFileName(doc.docPath) }}
                </div>
                <label class="doc-label">Статус</label>
                <b-form-select
                  v-model="doc.docStatus"
                  :options="docStatusOptions"
                  class="doc-status-select"
                  size="sm"
                />
                <label class="doc-label">Описание MLRO</label>
                <b-form-textarea
                  v-model="doc.mlroDesc"
                  rows="2"
                  placeholder="Введите описание..."
                  class="doc-textarea"
                />
                <div class="doc-actions">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    class="doc-btn"
                    @click="downloadDoc(doc)"
                  >
                    <b-icon icon="download"></b-icon>
                    Скачать
                  </b-button>
                  <b-button
                    size="sm"
                    variant="primary"
                    class="doc-btn doc-btn-update"
                    @click="updateDocMlro(doc)"
                  >
                    <b-icon icon="pencil"></b-icon>
                    Обновить
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Блокировка / разблокировка с обязательной причиной в обоих случаях -->
    <b-modal
      id="block-user-modal"
      :title="blockTarget && blockTarget.isActive == 1 ? 'Заблокировать пользователя' : 'Разблокировать пользователя'"
      :ok-title="blockTarget && blockTarget.isActive == 1 ? 'Заблокировать' : 'Разблокировать'"
      cancel-title="Отмена"
      :ok-variant="blockTarget && blockTarget.isActive == 1 ? 'danger' : 'success'"
      :ok-disabled="blockBusy || !blockReasonInput.trim()"
      @ok.prevent="confirmBlock"
      @hidden="onBlockModalHidden"
    >
      <div v-if="blockTarget">
        <p class="mb-2"><b>{{ blockTarget.email || blockTarget.userName || ('id ' + blockTarget.id) }}</b></p>
        <label class="small mb-1">
          Причина <span class="text-danger">(обязательно)</span>
        </label>
        <b-form-textarea
          v-model="blockReasonInput"
          rows="3"
          :placeholder="blockTarget.isActive == 1 ? 'Например: подозрительная активность, запрос compliance' : 'Например: проверка завершена, доступ восстановлен'"
        />
      </div>
    </b-modal>

    <!-- Заморозка / разморозка аккаунта с обязательным комментарием для аудита.
         Заморозка не запрещает вход в ЛК, но закрывает сервисы. -->
    <b-modal
      id="freeze-user-modal"
      :title="freezeTarget && isRowFrozen(freezeTarget) ? 'Разморозить аккаунт' : 'Заморозить аккаунт'"
      :ok-title="freezeTarget && isRowFrozen(freezeTarget) ? 'Разморозить' : 'Заморозить'"
      cancel-title="Отмена"
      :ok-variant="freezeTarget && isRowFrozen(freezeTarget) ? 'success' : 'warning'"
      :ok-disabled="freezeBusy || !freezeReasonInput.trim()"
      @ok.prevent="confirmFreeze"
      @hidden="onFreezeModalHidden"
    >
      <div v-if="freezeTarget">
        <p class="mb-2"><b>{{ freezeTarget.email || freezeTarget.userName || ('id ' + freezeTarget.id) }}</b></p>
        <p class="small text-muted mb-2" v-if="!isRowFrozen(freezeTarget)">
          Клиент сохранит доступ к личному кабинету, но будет полностью ограничен
          в сервисах (пополнение, покупка/продажа). Ему уйдёт уведомление.
        </p>
        <label class="small mb-1">
          Комментарий для внутреннего аудита <span class="text-danger">(обязательно)</span>
        </label>
        <b-form-textarea
          v-model="freezeReasonInput"
          rows="3"
          placeholder="Например: подозрительный источник средств по результатам KYT, тикет #123"
        />
      </div>
    </b-modal>

    <!-- ============ SLIDE-OUT USER PROFILE PANEL ============
         Row click opens the same UserDetail component in a fixed
         right-hand drawer. read-only mode = view only, no edits. -->
    <transition name="user-panel-fade">
      <div
        v-if="sidePanelOpen"
        class="user-panel-backdrop"
        @click.self="closeSidePanel"
      >
        <transition name="user-panel-slide">
          <aside
            v-if="sidePanelOpen"
            class="user-side-panel"
            role="dialog"
            aria-modal="true"
          >
            <!-- Small floating close (X) — accessibility shortcut so the
                 panel can be dismissed without scrolling all the way to
                 the footer. -->
            <button
              type="button"
              class="user-side-panel-close-x"
              aria-label="Закрыть"
              title="Закрыть"
              @click="closeSidePanel"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div class="user-side-panel-body">
              <UserDetail
                v-if="selectedUserId"
                :key="selectedUserId"
                :user-id="selectedUserId"
                :read-only="true"
                @user-updated="onSidePanelUserUpdated"
              />
            </div>

            <!-- Footer actions — port of KYC_interactive .sp-footer.
                 Closes the side panel, или открывает полное досье в
                 отдельной странице. -->
            <div class="user-side-panel-footer">
              <button
                type="button"
                class="sp-btn sp-btn-ghost"
                @click="closeSidePanel"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12"/>
                  <polyline points="12 19 5 12 12 5"/>
                </svg>
                Закрыть
              </button>
              <button
                type="button"
                class="sp-btn sp-btn-primary"
                @click="openFullPage"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
                Полное досье
              </button>
            </div>
          </aside>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from "axios";
import { apiDomain, getHeader } from "../config/config";
import { mapState } from "vuex";
import UserDetail from "./exchange/UserDetail.vue";

export default {
  name: "Users",
  components: { UserDetail },
  data() {
    return {
      // Slide-out side panel for viewing a user without leaving the list.
      sidePanelOpen: false,
      selectedUserId: null,
      // ─── KYC panel state ─────────────────────────────────────────────
      // Aggregate counters for the stat-cards row at the top, fetched
      // once on mount via /user/kyc-stats. Cards filter the table when
      // clicked (activeStatCard tracks which one is "selected").
      kycStats: { total: 0, active: 0, pending: 0, highRisk: 0, reclassification: 0, notIdentified: 0, individuals: 0, organizations: 0 },
      activeStatCard: 'total',
      // Mirrors the 5 stat-cards on the KYC_interactive mock.
      // 4-я карточка — Реклассификация (счётчик users с активной
      // PENDING-заявкой в p_classification_request).
      statCards: [
        { key: 'total',            label: 'Всего пользователей', icon: 'people-fill',              tone: 'neutral', hint: 'включая не прошедших KYC' },
        { key: 'active',           label: 'Active',              icon: 'check-circle-fill',        tone: 'success', hint: 'верифицированы' },
        { key: 'pending',          label: 'Pending',             icon: 'hourglass-split',          tone: 'warning', hint: 'ожидают проверки' },
        { key: 'reclassification', label: 'Реклассификация',     icon: 'arrow-repeat',             tone: 'warning', hint: 'заявки на рассмотрении' },
        { key: 'notIdentified',    label: 'Не верифицированы',   icon: 'shield-exclamation',       tone: 'info',    hint: 'KYC не пройден' },
      ],
      // Entity tabs — ФЛ / ЮЛ. Mirror isOrg (0 / 1). -1 ignored as a tab.
      entityTabs: [
        { value: 0, label: 'Физические лица' },
        { value: 1, label: 'Юридические лица' },
      ],
      // Quick filter chips above the table. Each chip flips a specific
      // backend filter onto a known value (or resets it).
      // Quick filter pills above the table. Order matches the
      // KYC_interactive mock; each chip flips a known backend filter
      // to a deterministic value (or resets it).
      quickChips: [
        { key: 'all',          label: 'Все',                tone: 'neutral' },
        { key: 'active',       label: 'Active',             tone: 'success' },
        { key: 'blocked',      label: 'Blocked',            tone: 'danger'  },
        { key: 'frozen',       label: 'Заморожены',         tone: 'warning' },
        { key: 'pending',      label: 'Pending Review',     tone: 'warning' },
        { key: 'reclass',      label: 'Reclassification',   tone: 'warning' },
        { key: 'high_risk',    label: 'Высокий риск',       tone: 'danger'  },
        { key: 'expiring',     label: 'Документы истекают', tone: 'danger'  },
        { key: 'not_verified', label: 'Не верифицированы',  tone: 'info'    },
      ],
      activeChip: 'all',
      // Advanced filter panel toggle (the legacy big filter block lives
      // behind the "Фильтр" button now).
      showAdvancedFilters: false,

      // KYC-panel columns — matches the product mock-up (KYC_interactive).
      // Each row is clickable (navigates to /userdetail), checkboxes drive
      // bulk actions, the leftmost colored stripe encodes risk level via
      // tbody-tr-class -> kyc-row--risk-<low|medium|high>.
      fields: [
        { key: "select",         label: "",                  thStyle: { width: "36px" } },
        { key: "email",          label: "Email" },
        { key: "fullName",       label: "ФИО" },
        { key: "iin",            label: "ИИН" },
        { key: "userType",       label: "Тип" },
        { key: "riskLevel",      label: "Риск" },
        { key: "classification", label: "Классификация" },
        { key: "limit",          label: "Лимит/мес" },
        { key: "status",         label: "Статус" },
        { key: "actions",        label: "",                  thStyle: { width: "1px" } },
      ],
      // Bulk-selection state — ids of users ticked in the leftmost
      // column, used by the bulk-actions bar above the table.
      selectedRowIds: [],

      // Block / unblock modal state. Reason mandatory when blocking.
      blockTarget: null,
      blockReasonInput: '',
      blockBusy: false,
      // Заморозка аккаунта — обязательный комментарий для аудита.
      freezeTarget: null,
      freezeReasonInput: '',
      freezeBusy: false,

      isBusy: false,
      totalRows: 0,
      currentPage: 1,
      perPage: 20,
      tableVariant: "light",
      filter: "",
      // KYC-panel filter state. Only the ones with values are sent to
      // the backend today; everything else is a placeholder waiting for
      // its real source.
      filterNationality: null,
      filterOnboarding: null,
      filterClassification: null,
      filterBlocked: null,
      filterFrozen: null,
      // 1 = only users with an active classification request,
      // null = "do not filter on it".
      filterReclass: null,
      // ── KYC-панель advanced filters (KYC_interactive mock) ──────
      // Synthetic account-status bucket — see backend AccountStatus.
      filterAccountStatus: '',
      filterRiskLevel: '',
      filterResidency: '',
      filterCreatedFrom: '',
      filterCreatedTo: '',
      // Quick-chip-driven; 0 = off, 1 = only users with KYC docs
      // expiring within the next 30 days.
      filterExpiringDocs: 0,
      // Date-range picker presets shown in the filter card.
      datePresetOptions: [
        { value: '',          text: 'Все время' },
        { value: 'today',     text: 'Сегодня' },
        { value: 'yesterday', text: 'Вчера' },
        { value: 'week',      text: 'Эта неделя' },
        { value: 'lastweek',  text: 'Прошлая неделя' },
        { value: 'month',     text: 'Прошлый месяц' },
        { value: 'year',      text: 'Прошлый год' },
      ],
      datePreset: '',
      accountStatusOptions: [
        { value: '',          text: 'Все статусы' },
        { value: 'ACTIVE',    text: 'Active' },
        { value: 'PENDING',   text: 'Pending' },
        { value: 'REJECTED',  text: 'Rejected' },
        { value: 'BLOCKED',   text: 'Blocked' },
      ],
      riskLevelOptions: [
        { value: '',       text: 'Все' },
        { value: 'MEDIUM', text: 'Средний' },
        { value: 'HIGH',   text: 'Высокий' },
      ],
      residencyOptions: [
        { value: '',             text: 'Все' },
        { value: 'RESIDENT',     text: 'Резидент РК' },
        { value: 'NON_RESIDENT', text: 'Нерезидент' },
      ],
      nationalityOptions: [
        { value: null, text: "Все" },
        { value: "KZ", text: "Казахстан (KZ)" },
        { value: "RU", text: "Россия (RU)" },
        { value: "UZ", text: "Узбекистан (UZ)" },
        { value: "KG", text: "Кыргызстан (KG)" },
      ],
      onboardingOptions: [
        { value: null, text: "Все" },
        { value: "PENDING", text: "PENDING" },
        { value: "APPROVED", text: "APPROVED" },
        { value: "REJECTED", text: "REJECTED" },
      ],
      classificationOptions: [
        { value: null, text: "Все" },
        { value: "NONE", text: "Не назначен" },
        { value: "RETAIL_NQ", text: "Розничный (неквалиф.)" },
        { value: "RETAIL_Q", text: "Розничный (квалиф.)" },
        { value: "PROFESSIONAL", text: "Профессиональный" },
      ],
      blockedOptions: [
        { value: null, text: "Все" },
        { value: 0, text: "Заблокирован" },
        { value: 1, text: "Активен" },
      ],
      userTypeOptions: [
        { value: -1, text: "Все" },
        { value: 0, text: "Физическое лицо" },
        { value: 1, text: "Юридическое лицо" },
      ],
      selectMode: "multi",
      selectedRows: [],

      userForm: {
        id: 0,
        thirdName: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        password: "",
        roleId: 0,
        userRoles: [],
      },

      roles: [],
      roleId: 0,
      isOrg: 0,

      // Docs modal state
      docsLoading: false,
      docsData: null,
      docsError: null,
      docsSelectedUser: null,
      docsType: null,

      docStatusOptions: [
        { value: "PENDING", text: "Ожидание" },
        { value: "APPROVED", text: "Принять" },
        { value: "REJECTED", text: "Отклонить" },
      ],
    };
  },

  computed: {
    ...mapState(["loginedUser"]),
    docsModalTitle() {
      const typeLabel = this.docsType === "qualified" ? "Квалификационные документы" : "Профессиональные документы";
      const name = this.docsSelectedUser ? (this.docsSelectedUser.displayName || this.docsSelectedUser.email) : "";
      return name ? `${typeLabel} — ${name}` : typeLabel;
    },
    docsList() {
      if (!this.docsData) return [];
      const d = this.docsData;
      return Array.isArray(d) ? d : [d];
    },
    // Bulk selection helpers — used by the "select all on page" checkbox
    // in the table header. ids on the current page come from the table
    // ref's localItems (b-table exposes them after the provider resolves).
    currentPageIds() {
      const t = this.$refs.userTable
      const items = (t && t.localItems) || []
      return items.map(i => i && i.id).filter(Boolean)
    },
    allOnPageSelected() {
      const ids = this.currentPageIds
      return ids.length > 0 && ids.every(id => this.selectedRowIds.indexOf(id) >= 0)
    },
    someOnPageSelected() {
      const ids = this.currentPageIds
      const hit = ids.filter(id => this.selectedRowIds.indexOf(id) >= 0).length
      return hit > 0 && hit < ids.length
    },
  },

  methods: {
    // ── KYC panel helpers ──────────────────────────────────────────
    loadKycStats() {
      axios
        .get(`${apiDomain}/user/kyc-stats`, { headers: getHeader() })
        .then(res => { this.kycStats = res.data || this.kycStats })
        .catch(() => {})
    },
    kycStatValue(key) {
      const v = this.kycStats[key]
      return (v == null ? 0 : Number(v))
    },
    onStatCardClick(card) {
      // Card "selection" filters the table via the matching chip.
      // Selecting the same card again resets the filter back to "all".
      const same = this.activeStatCard === card.key
      this.activeStatCard = same ? 'total' : card.key
      const chipKey = same ? 'all' : ({
        active: 'active',
        pending: 'pending',
        reclassification: 'reclass',
        notIdentified: 'not_verified',
        total: 'all',
      })[card.key] || 'all'
      this.onChipClick(this.quickChips.find(c => c.key === chipKey) || this.quickChips[0])
    },
    setEntityTab(value) {
      this.isOrg = value
      this.applyFilters()
    },
    entityTabCount(value) {
      return value === 1 ? this.kycStats.organizations : this.kycStats.individuals
    },
    // Chips flip specific filters; "Все" resets them. The chip keys are
    // intentionally narrow — each maps to one or two backend fields so
    // the analyst's intent is unambiguous.
    onChipClick(chip) {
      this.activeChip = chip.key
      // Reset chip-controlled fields to a known baseline first.
      this.filterOnboarding = null
      this.filterBlocked = null
      this.filterFrozen = null
      this.filterClassification = null
      this.filterReclass = null
      this.filterRiskLevel = ''
      this.filterExpiringDocs = 0
      this.filterAccountStatus = ''
      switch (chip.key) {
        case 'active':       this.filterOnboarding = 'APPROVED'; this.filterBlocked = 1; break
        case 'frozen':       this.filterFrozen = 1; break
        // «Pending» = onboarding на проверке ИЛИ заявка ждёт MLRO —
        // это синтетический AccountStatus, совпадает со счётчиком карточки.
        case 'pending':      this.filterAccountStatus = 'PENDING'; break
        case 'reclass':      this.filterReclass = 1; break
        case 'high_risk':    this.filterRiskLevel = 'HIGH'; break
        case 'blocked':      this.filterBlocked = 0; break
        case 'expiring':     this.filterExpiringDocs = 1; break
        case 'not_verified': this.filterOnboarding = 'NOTIDENTIFIED'; break
        // 'all' just resets, which we already did above
      }
      // Sync the matching stat-card highlight.
      const reverse = ({
        active: 'active', pending: 'pending', reclass: 'reclassification',
        not_verified: 'notIdentified', all: 'total',
      })[chip.key]
      if (reverse) this.activeStatCard = reverse
      this.applyFilters()
    },
    exportCsv() {
      // Placeholder until the dedicated export endpoint lands.
      this.$bvToast && this.$bvToast.toast('Экспорт CSV в разработке.', {
        title: 'Скоро будет', variant: 'info',
        toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 2500,
      })
    },

    // ── Row helpers (formatting, risk, status) ───────────────────
    fullName(item) {
      const parts = [item.lastName, item.firstName, item.thirdName].filter(Boolean)
      const joined = parts.join(' ').trim()
      return joined || item.displayName || item.userName || '—'
    },
    // Приоритет — ручное переопределение комплаенса (risk_override:
    // MEDIUM/HIGH/CRITICAL/LOW). При отсутствии override — прокси:
    // PEP=high, rejected=high, всё остальное — medium (базовый уровень).
    riskLevel(item) {
      const ov = item.riskOverride
      if (ov) {
        const v = String(ov).toUpperCase()
        if (v === 'HIGH' || v === 'CRITICAL') return 'high'
        if (v === 'MEDIUM') return 'medium'
        if (v === 'LOW') return 'low'
      }
      if (item.isPep === 1 || item.isPep === '1') return 'high'
      if ((item.onboardingStatus || '').toUpperCase() === 'REJECTED') return 'high'
      return 'medium'
    },
    riskLabel(item) {
      return ({ low: 'Низкий', medium: 'Средний', high: 'Высокий' })[this.riskLevel(item)]
    },
    rowClassByRisk(item) {
      if (!item) return ''
      return 'kyc-row kyc-row--risk-' + this.riskLevel(item)
    },
    classificationLabel(code) {
      const m = {
        NONE:         'Не назначен',
        RETAIL_NQ:    'Розничный (неквалиф.)',
        RETAIL_Q:     'Розничный (квалиф.)',
        PROFESSIONAL: 'Профессиональный',
      }
      return m[code] || code || '—'
    },
    statusInfo(item) {
      if (item.isActive == 0) return { label: 'Blocked', tone: 'danger' }
      switch ((item.onboardingStatus || '').toUpperCase()) {
        case 'APPROVED':      return { label: 'Active',   tone: 'success' }
        case 'PENDING':       return { label: 'Pending',  tone: 'warning' }
        case 'REJECTED':      return { label: 'Rejected', tone: 'danger'  }
        case 'NOTIDENTIFIED': return { label: 'New',      tone: 'info'    }
        default:              return { label: '—',        tone: 'secondary' }
      }
    },
    limitDisplay(item) {
      const v = Number(item.limitInvestment || 0)
      if (v >= 1000000) return 'Без лимита'
      if (!v) return '0 $'
      return v.toLocaleString('ru-RU') + ' $'
    },
    // ── Selection (bulk-bar) ─────────────────────────────────────
    isSelected(id) {
      return this.selectedRowIds.indexOf(id) >= 0
    },
    toggleSelect(id) {
      const idx = this.selectedRowIds.indexOf(id)
      if (idx >= 0) this.selectedRowIds.splice(idx, 1)
      else this.selectedRowIds.push(id)
    },
    toggleAllOnPage() {
      const ids = this.currentPageIds
      if (this.allOnPageSelected) {
        this.selectedRowIds = this.selectedRowIds.filter(id => ids.indexOf(id) < 0)
      } else {
        const set = new Set(this.selectedRowIds.concat(ids))
        this.selectedRowIds = Array.from(set)
      }
    },
    clearSelection() {
      this.selectedRowIds = []
    },
    exportSelected() {
      this.$bvToast && this.$bvToast.toast(
        `Экспорт ${this.selectedRowIds.length} записей будет добавлен позже.`,
        { title: 'В разработке', variant: 'info', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 2500 }
      )
    },
    bulkBlockSelected() {
      // Naive bulk — sequential block calls. UI confirmation later.
      const ids = this.selectedRowIds.slice()
      Promise.all(ids.map(id => axios.post(apiDomain + '/user/changestatus', { userId: id }, { headers: getHeader() }).catch(() => null)))
        .then(() => {
          this.clearSelection()
          this.$refs.userTable && this.$refs.userTable.refresh()
          this.loadKycStats()
        })
    },
    // ── Row click → profile ──────────────────────────────────────
    // Used by b-table @row-clicked. Clicks on the checkbox / action
    // buttons stop propagation themselves (see templates), so this
    // fires only for "rest of the row".
    onRowClicked(item) {
      this.openUserCard(item)
    },

    sysChange() {
      this.$refs.userTable.refresh();
    },

    onUserSelected(items) {
      this.selectedRows = items;
    },

    viewDetails(item) {
      // ✅ Тут сделай что нужно: роут, модалка, или запрос деталей
      // пример: перейти на страницу деталей
      this.$router.push({ name: "UserDetail", params: { userId: item.id } });
      
    },

    deleteRecord() {
      const warn = confirm("Вы уверены?");
      if (!warn) return;

      if (this.selectedRows.length > 0) {
        axios
          .post(apiDomain + "/user/delete", this.selectedRows, { headers: getHeader() })
          .then(() => this.$refs.userTable.refresh());
      }
    },

    updateRecord() {
      if (this.selectedRows.length !== 1) return;

      const id = this.selectedRows[0].id;
      axios
        .post(apiDomain + "/user/current", { id }, { headers: getHeader() })
        .then((response) => {
          this.userForm = response.data;
          this.$bvModal.show("userModal");
        });
    },

    modalHidden() {
      this.userForm = {
        id: 0,
        thirdName: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        password: "",
        roleId: 0,
        userRoles: [],
      };
    },

    submitUser(evt) {
      evt.preventDefault();

      this.userForm.userRoles = [];
      if (Number(this.userForm.roleId) === 0) {
        alert("Пожалуйста, выберите роль");
        return;
      }

      this.userForm.userRoles.push({ id: this.userForm.roleId });

      axios
        .post(apiDomain + "/user/create", this.userForm, { headers: getHeader() })
        .then((response) => {
          if (response.data === "dublicated") {
            alert("Email уже зарегистрирован.");
            return;
          }
          this.$bvModal.hide("userModal");
          this.modalHidden();
          this.$refs.userTable.refresh();
        })
        .catch(() => alert("Ошибка"));
    },

    randomizeUser(userId) {
      axios
        .post(apiDomain + "/user/test/randomize", { userId }, { headers: getHeader() })
        .then(() => {
          this.$refs.userTable.refresh();
        })
        .catch((err) => {
          alert("Ошибка рандомизации: " + ((err.response && err.response.data && err.response.data.message) || err.message));
        });
    },

    // Открываем модалку блокировки/разблокировки. Причина обязательна
    // в обоих случаях (пишется в append-only block-log на бэке).
    askBlock(item) {
      this.blockTarget = item;
      this.blockReasonInput = "";
      this.$bvModal.show("block-user-modal");
    },
    onBlockModalHidden() {
      this.blockTarget = null;
      this.blockReasonInput = "";
    },
    confirmBlock() {
      const item = this.blockTarget;
      if (!item) return;
      const reason = this.blockReasonInput.trim();
      if (!reason) return; // причина обязательна и для блокировки, и для разблокировки
      this.blockBusy = true;
      axios
        .post(apiDomain + "/user/changestatus", { userId: item.id, reason: reason }, { headers: getHeader() })
        .then(() => {
          this.$bvModal.hide("block-user-modal");
          this.$refs.userTable.refresh();
        })
        .catch((err) => {
          const sc = err && err.response && err.response.status;
          this.$bvToast.toast(sc === 401 || sc === 403 ? "Недостаточно прав." : "Не удалось изменить статус.", {
            title: "Ошибка", variant: "danger", toaster: "b-toaster-top-center", solid: true, autoHideDelay: 4500,
          });
        })
        .finally(() => { this.blockBusy = false; });
    },

    // ---- Заморозка / разморозка аккаунта ----
    isRowFrozen(item) {
      const v = item && item.accountFrozen;
      return v === 1 || v === '1' || v === true;
    },
    askFreeze(item) {
      this.freezeTarget = item;
      this.freezeReasonInput = "";
      this.$bvModal.show("freeze-user-modal");
    },
    onFreezeModalHidden() {
      this.freezeTarget = null;
      this.freezeReasonInput = "";
    },
    confirmFreeze() {
      const item = this.freezeTarget;
      if (!item) return;
      const reason = this.freezeReasonInput.trim();
      if (!reason) return; // комментарий обязателен
      const frozen = !this.isRowFrozen(item);
      this.freezeBusy = true;
      axios
        .post(apiDomain + "/user/freeze-status", { userId: item.id, frozen: frozen, reason: reason }, { headers: getHeader() })
        .then(() => {
          this.$bvModal.hide("freeze-user-modal");
          this.$bvToast.toast(frozen ? "Аккаунт заморожен. Клиент уведомлён." : "Заморозка снята. Клиент уведомлён.", {
            title: "Готово", variant: "success", toaster: "b-toaster-top-center", solid: true, autoHideDelay: 2500,
          });
          this.$refs.userTable.refresh();
        })
        .catch((err) => {
          const sc = err && err.response && err.response.status;
          this.$bvToast.toast(sc === 401 || sc === 403 ? "Недостаточно прав." : "Не удалось изменить состояние заморозки.", {
            title: "Ошибка", variant: "danger", toaster: "b-toaster-top-center", solid: true, autoHideDelay: 4500,
          });
        })
        .finally(() => { this.freezeBusy = false; });
    },

    openDocsModal(user, docType) {
      this.docsSelectedUser = user;
      this.docsType = docType;
      this.docsData = null;
      this.docsError = null;
      this.$bvModal.show("docsModal");
      this.fetchClientDocs(user.id, docType);
    },

    // Same API for qualification (QUALIFIED) and professional (PROFESSIONAL); only docType in path differs
    fetchClientDocs(userId, docType) {
      this.docsLoading = true;
      this.docsError = null;
      const url = `${apiDomain}/ptest/clientdocs/${docType.toUpperCase()}/${userId}`;
      axios
        .get(url, { headers: getHeader() })
        .then((response) => {
          this.docsData = response.data;
          this.docsLoading = false;
        })
        .catch((err) => {
          this.docsLoading = false;
          this.docsError = (err.response && err.response.data && err.response.data.message) || err.message || "Ошибка загрузки документов";
        });
    },

    docsModalHidden() {
      this.docsSelectedUser = null;
      this.docsType = null;
      this.docsData = null;
      this.docsError = null;
    },

    docStatusVariant(status) {
      if (!status) return "secondary";
      const s = String(status).toUpperCase();
      if (s === "PENDING") return "warning";
      if (s === "APPROVED" || s === "ACTIVE") return "success";
      if (s === "REJECTED" || s === "DECLINED") return "danger";
      return "secondary";
    },

    docFileName(path) {
      if (!path) return "—";
      const parts = path.replace(/\\/g, "/").split("/");
      return parts[parts.length - 1] || path;
    },

    docTypeLabel(type) {
      // Human-friendly Russian labels for the docType enum stored in
      // p_client_doc. Fall back to the raw value for anything we
      // haven't mapped yet so unknown types stay visible.
      const map = {
        QUALIFIED:            "Источник средств (SoF)",
        CERTIFICATE:          "Сертификат (квалификация)",
        PROFESSIONAL:         "Профессиональный пакет",
        PROFESSIONAL_HISTORY: "История торгов",
        PROFESSIONAL_SOF:     "Источник средств / благосостояния (SoF / SoW)",
        PROFESSIONAL_CERT:    "Сертификат (Professional)",
      };
      return map[type] || type || "—";
    },

    downloadDoc(doc) {
      const fileName = this.docFileName(doc.docPath);
      if (!fileName || fileName === "—") return;
      const url = apiDomain + "/ptest/downloadclientfile/" + encodeURIComponent(fileName);
      axios
        .get(url, { headers: getHeader(), responseType: "blob" })
        .then((response) => {
          const blob = new Blob([response.data], { type: response.data.type || "application/octet-stream" });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
          window.URL.revokeObjectURL(link.href);
        })
        .catch(() => {
          alert("Ошибка при скачивании файла.");
        });
    },

    // Same API for qualification and professional; only docType in body differs
    updateDocMlro(doc) {
      const userId = doc.userId != null ? doc.userId : (this.docsSelectedUser && this.docsSelectedUser.id);
      if (userId == null) return;
      const body = {
        docType: doc.docType || this.docsType.toUpperCase(),
        userId: userId,
        status: doc.docStatus || "PENDING",
        docId: doc.docId,
        mlroDesc: doc.mlroDesc || "",
      };
      axios
        .post(apiDomain + "/ptest/changeclientstatus", body, { headers: getHeader() })
        .then(() => {
          this.fetchClientDocs(this.docsSelectedUser.id, this.docsType);
        })
        .catch(() => {
          alert("Ошибка при обновлении.");
        });
    },

    userProvider(ctx) {
      this.isBusy = true;
      ctx.roleId = this.roleId;
      ctx.isOrg = this.isOrg;
      // Forward the new KYC-panel filter fields. Backend can add real
      // filtering on top of these next — the JSON shape is already in
      // place so the wire contract doesn't churn.
      ctx.nationality = this.filterNationality || null;
      ctx.onboardingStatus = this.filterOnboarding || null;
      ctx.clientCategory = this.filterClassification || null;
      ctx.isActive = this.filterBlocked;
      ctx.accountFrozen = this.filterFrozen;
      // Reclassification chip: 1 = only users with an active
      // classification request, null = no filter.
      ctx.hasActiveReclass = this.filterReclass != null ? Number(this.filterReclass) : null;
      // ── KYC-панель advanced filters (KYC_interactive mock) ────
      ctx.accountStatus = this.filterAccountStatus || '';
      ctx.riskLevel     = this.filterRiskLevel     || '';
      ctx.residency     = this.filterResidency     || '';
      ctx.createdFrom   = this.filterCreatedFrom   || '';
      ctx.createdTo     = this.filterCreatedTo     || '';
      ctx.expiringDocs  = this.filterExpiringDocs ? 1 : null;
      // KYC panel is client-only — system users (ADMIN / MANAGER /
      // MLRO / OPERATOR) get their own page now.
      ctx.audience = 'clients';
      this._pageStart = (this.currentPage - 1) * this.perPage;
      return axios
        .post(apiDomain + "/user/list", ctx, { headers: getHeader() })
        .then((response) => {
          const result = response.data;
          this.totalRows = result.recordCount;
          this.isBusy = false;
          return result.items;
        })
        .catch(() => {
          this.isBusy = false;
          return [];
        });
    },
    rowNumber(idx) {
      return (this._pageStart || 0) + idx + 1;
    },
    applyFilters() {
      this.currentPage = 1;
      this.$refs.userTable && this.$refs.userTable.refresh();
    },
    resetFilters() {
      this.filter = "";
      this.filterNationality = null;
      this.filterOnboarding = null;
      this.filterClassification = null;
      this.filterBlocked = null;
      this.filterFrozen = null;
      this.filterAccountStatus = '';
      this.filterRiskLevel = '';
      this.filterResidency = '';
      this.filterCreatedFrom = '';
      this.filterCreatedTo = '';
      this.filterExpiringDocs = 0;
      this.datePreset = '';
      this.filterReclass = null;
      this.activeChip = 'all';
      this.activeStatCard = 'total';
      this.isOrg = 0;
      this.roleId = 0;
      this.applyFilters();
    },
    // Date-range presets driven by the filter card "Период регистрации"
    // dropdown. Sets filterCreatedFrom / filterCreatedTo to ISO dates,
    // then re-applies the table query.
    onDatePreset(preset) {
      const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      const today = new Date();
      const startOfWeek = (d) => {
        // Monday-start, matches the mock copy "Эта неделя".
        const dt = new Date(d); const day = (dt.getDay() + 6) % 7;
        dt.setDate(dt.getDate() - day); return dt;
      };
      let from = '', to = '';
      switch (preset) {
        case 'today':
          from = to = fmt(today); break;
        case 'yesterday': {
          const y = new Date(today); y.setDate(y.getDate() - 1);
          from = to = fmt(y); break;
        }
        case 'week': {
          const s = startOfWeek(today);
          from = fmt(s); to = fmt(today); break;
        }
        case 'lastweek': {
          const s = startOfWeek(today); s.setDate(s.getDate() - 7);
          const e = new Date(s); e.setDate(s.getDate() + 6);
          from = fmt(s); to = fmt(e); break;
        }
        case 'month': {
          // Previous calendar month.
          const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const e = new Date(today.getFullYear(), today.getMonth(), 0);
          from = fmt(s); to = fmt(e); break;
        }
        case 'year': {
          const y = today.getFullYear() - 1;
          from = `${y}-01-01`; to = `${y}-12-31`; break;
        }
        // '' = "Все время" → both stay empty.
      }
      this.datePreset = preset;
      this.filterCreatedFrom = from;
      this.filterCreatedTo = to;
      this.applyFilters();
    },
    openUserCard(item) {
      // Open the same UserDetail component in a read-only side panel
      // instead of routing away from the list. Esc / backdrop / X
      // close it; no URL changes — keeping filters and scroll intact.
      if (!item || !item.id) return;
      this.selectedUserId = item.id;
      this.sidePanelOpen = true;
      if (typeof document !== 'undefined') document.body.style.overflow = 'hidden';
    },
    closeSidePanel() {
      this.sidePanelOpen = false;
      this.selectedUserId = null;
      if (typeof document !== 'undefined') document.body.style.overflow = '';
    },
    // Fired by the embedded UserDetail after a reclassification decision:
    // re-fetch the list so the row's "Лимит/мес" / категория / статус
    // update live, without the admin having to reload the page. KYC
    // stats chips are recomputed for the same reason.
    onSidePanelUserUpdated() {
      this.$refs.userTable && this.$refs.userTable.refresh();
      if (typeof this.loadKycStats === 'function') this.loadKycStats();
    },
    // "Open in full page" button next to the close. Closes the panel
    // (otherwise the page would render behind the still-open overlay)
    // and routes to /userdetail/:userId where the same component
    // mounts in standalone mode.
    openFullPage() {
      const id = this.selectedUserId;
      if (!id) return;
      this.closeSidePanel();
      this.$router.push({ name: 'UserDetail', params: { userId: id } }).catch(() => {});
    },
    onSidePanelKeydown(e) {
      if (e.key === 'Escape' && this.sidePanelOpen) this.closeSidePanel();
    },

    getRefs() {
      axios.post(apiDomain + "/user/rolelist", {}, { headers: getHeader() }).then((response) => {
        this.roles = response.data;
      });
    },
  },

  created() {
    this.getRefs();
    this.loadKycStats();
  },

  mounted() {
    window.addEventListener('keydown', this.onSidePanelKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onSidePanelKeydown);
    if (typeof document !== 'undefined') document.body.style.overflow = '';
  },
};
</script>

<style scoped>
.users-page {
  padding: 8px 6px;
}

.page-title {
  padding: 4px 2px;
}

.title-text {
  font-weight: 700;
  letter-spacing: 0.2px;
  color: #1f2937;
}

.title-hint {
  display: inline-block;
  margin-top: 2px;
}

.toolbar-card,
.table-card {
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.04);
}

.total-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.08);
  color: #1f2937;
}

.btn-soft {
  border-radius: 10px;
}

.btn-round {
  border-radius: 10px;
}

.gap-8 {
  gap: 8px;
}

.badge-soft {
  background: rgba(99, 102, 241, 0.12);
  color: #3730a3;
  border-radius: 999px;
  padding: 6px 10px;
  font-weight: 600;
}

.btn-doc {
  border-radius: 8px;
  font-size: 0.8rem;
}

/* KYC-panel header + filter card per product mock-up */
.kyc-breadcrumb {
  background: linear-gradient(180deg, #f3f5fa 0%, #e8ecf4 100%);
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 14px;
  color: #36404f;
}
.kyc-breadcrumb-root {
  font-weight: 600;
  color: #1f2d44;
}
/* Filter card — port of KYC_interactive .filter-card.
   Clean white surface, neutral hairline border, small uppercase
   eyebrow labels, soft-fill inputs, green accent on focus + primary
   action. Animated slide-in on toggle. */
.kyc-filters-card {
  background: #fff;
  border: 1px solid #EAECF0;
  border-radius: 12px;
  padding: 18px 20px 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  animation: kycFilterSlideDown 0.18s ease;
}
@keyframes kycFilterSlideDown {
  from { opacity: 0; transform: translateY(-5px); }
  to   { opacity: 1; transform: translateY(0); }
}
.kyc-filters-card .card-body { padding: 0 !important; }

/* Eyebrow labels above each field. */
.kyc-filter-label {
  font-size: 9px;
  font-weight: 700;
  color: #9CA3AF;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  margin-bottom: 5px;
  display: block;
}

/* Custom shells for the input + select inside the filter card. We
   target via the .kyc-filters-card scope so the rest of the admin
   stays on its default BootstrapVue look. */
.kyc-filters-card .form-control,
.kyc-filters-card .custom-select {
  height: 34px;
  padding: 0 11px;
  border: 1px solid #EAECF0;
  border-radius: 8px;
  background: #F9FAFB;
  font-size: 12px;
  font-weight: 500;
  color: #111;
  box-shadow: none;
  transition: border-color .15s ease, background-color .15s ease, box-shadow .15s ease;
}
.kyc-filters-card .form-control:focus,
.kyc-filters-card .custom-select:focus {
  border-color: #16A37F;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(22, 163, 127, 0.08);
  outline: none;
}
.kyc-filters-card .form-control::placeholder { color: #C0C4CC; font-weight: 400; }
.kyc-filters-card .custom-select {
  /* Inline chevron + extra right padding so the v-html chevron
     BootstrapVue ships doesn't visually collide with the value. */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%23aaa' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 28px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
}

.kyc-filters-divider {
  border: 0;
  border-top: 1px solid #F0F2F5;
  margin: 6px -20px 14px;
}

/* Footer actions: reset = ghost button, primary = green accent
   matching the KYC_interactive `.btn-search` mock. */
.kyc-filters-card .btn-outline-secondary {
  height: 34px;
  padding: 0 16px;
  background: #fff;
  border: 1px solid #EAECF0;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #6B7280;
  transition: all .15s ease;
}
.kyc-filters-card .btn-outline-secondary:hover {
  background: #F9FAFB;
  color: #374151;
  border-color: #D1D5DB;
}
.kyc-filters-card .btn-primary {
  height: 34px;
  padding: 0 20px;
  background: #16A37F;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  box-shadow: 0 1px 2px rgba(22, 163, 127, 0.25);
  transition: background-color .15s ease, transform .12s ease;
}
.kyc-filters-card .btn-primary:hover,
.kyc-filters-card .btn-primary:focus {
  background: #138C6C;
  box-shadow: 0 0 0 3px rgba(22, 163, 127, 0.18);
  transform: translateY(-1px);
}
.kyc-filters-card .btn-primary:active { transform: translateY(0); }
.kyc-th-narrow {
  width: 40px;
  text-align: center;
}

/* ── KYC page header ─────────────────────────────────────────────── */
.kyc-page-title {
  font-weight: 800;
  color: #1f2d44;
  font-size: 26px;
  letter-spacing: -0.01em;
}
.kyc-page-subtitle {
  color: #6b7280;
  font-size: 13px;
}
.kyc-export-btn {
  font-weight: 600;
  border-radius: 8px;
}

/* ── Stat cards row ──────────────────────────────────────────────── */
.kyc-stats-row {
  margin-left: -6px;
  margin-right: -6px;
}
.kyc-stat-col {
  padding-left: 6px;
  padding-right: 6px;
  display: flex;
}
.kyc-stat-card {
  background: #fff;
  border: 1px solid #e4e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
}
.kyc-stat-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
}
.kyc-stat-card.is-active {
  border-color: #16a34a;
  box-shadow: 0 8px 20px rgba(22, 163, 74, 0.18);
}
.kyc-stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f3f4f6;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.kyc-stat-card.kyc-stat--success .kyc-stat-icon { background: #dcfce7; color: #16a34a; }
.kyc-stat-card.kyc-stat--warning .kyc-stat-icon { background: #fef3c7; color: #d97706; }
.kyc-stat-card.kyc-stat--danger  .kyc-stat-icon { background: #fee2e2; color: #dc2626; }
.kyc-stat-card.kyc-stat--info    .kyc-stat-icon { background: #dbeafe; color: #2563eb; }
.kyc-stat-body {
  flex: 1;
  min-width: 0;
}
.kyc-stat-label {
  color: #6b7280;
  font-size: 12px;
  margin-bottom: 4px;
}
.kyc-stat-value {
  font-weight: 800;
  font-size: 26px;
  color: #1f2d44;
  line-height: 1;
}
.kyc-stat-value.text-success { color: #16a34a; }
.kyc-stat-value.text-warning { color: #d97706; }
.kyc-stat-value.text-danger  { color: #dc2626; }
.kyc-stat-value.text-info    { color: #2563eb; }
.kyc-stat-value.text-neutral { color: #1f2d44; }
.kyc-stat-hint {
  color: #94a3b8;
  font-size: 11px;
  margin-top: 4px;
}

/* ── Tabs (ФЛ / ЮЛ) ──────────────────────────────────────────────── */
.kyc-tabs {
  display: flex;
  gap: 0;
  border-radius: 10px;
  background: #f8fafc;
  padding: 4px;
  border: 1px solid #e4e8f0;
}
.kyc-tab {
  background: transparent;
  border: 0;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.kyc-tab:hover { color: #1f2d44; }
.kyc-tab.active {
  background: #fff;
  color: #1f2d44;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06);
}
.kyc-tab-count {
  background: #e2e8f0;
  color: #475569;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 700;
}
.kyc-tab.active .kyc-tab-count {
  background: #16a34a;
  color: #fff;
}
/* Filter-toggle button — ported from KYC_interactive .filter-toggle-btn.
   Ghost look idle, green-accent on hover, full-green when active so it
   reads as an "ON" state matching the open filter card below it. */
.kyc-filter-btn.btn,
.kyc-filter-btn.btn.btn-outline-secondary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #EAECF0;
  color: #374151;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  box-shadow: none;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
}
.kyc-filter-btn.btn:hover,
.kyc-filter-btn.btn:focus {
  background: #fff;
  border-color: #16A37F;
  color: #16A37F;
  box-shadow: 0 0 0 3px rgba(22, 163, 127, 0.08);
}
.kyc-filter-btn.btn.is-active {
  background: #16A37F;
  border-color: #16A37F;
  color: #fff;
}
.kyc-filter-btn.btn.is-active:hover,
.kyc-filter-btn.btn.is-active:focus {
  background: #138C6C;
  border-color: #138C6C;
  color: #fff;
}
.kyc-filter-btn .kyc-filter-btn-icon { font-size: 13px; }
.kyc-filter-btn .kyc-filter-btn-chevron {
  font-size: 12px;
  margin-left: 1px;
  transition: transform .2s ease;
}
.kyc-filter-btn.is-active .kyc-filter-btn-chevron { transform: rotate(180deg); }

/* ── Quick chips ─────────────────────────────────────────────────── */
.kyc-chips-row {
  gap: 6px;
}
.kyc-chips-label {
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
}
.kyc-chip {
  background: #fff;
  border: 1px solid #e4e8f0;
  border-radius: 999px;
  padding: 4px 12px 4px 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease;
}
.kyc-chip:hover { border-color: #cbd5e1; }
.kyc-chip.active {
  background: #1f2d44;
  color: #fff;
  border-color: #1f2d44;
}
.kyc-chip.active .kyc-chip-dot { background: #fff; opacity: 0.85; }
.kyc-chip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #cbd5e1;
  display: inline-block;
}
.kyc-chip.kyc-chip--neutral .kyc-chip-dot { background: #6b7280; }
.kyc-chip.kyc-chip--success .kyc-chip-dot { background: #16a34a; }
.kyc-chip.kyc-chip--warning .kyc-chip-dot { background: #f59e0b; }
.kyc-chip.kyc-chip--danger  .kyc-chip-dot { background: #dc2626; }
.kyc-chip.kyc-chip--dark    .kyc-chip-dot { background: #0f172a; }
.kyc-chip.kyc-chip--info    .kyc-chip-dot { background: #2563eb; }
.kyc-chip.kyc-chip--primary .kyc-chip-dot { background: #7c3aed; }

/* ── Bulk-actions bar (shown when rows are checked) ───────────── */
.kyc-bulk-bar {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  padding: 10px 14px;
}

/* ── KYC users table chrome ──────────────────────────────────── */
.kyc-users-table tbody tr {
  cursor: pointer;
  border-left: 3px solid transparent;
}
.kyc-users-table tbody tr:hover {
  background: #f8fafc;
}
.kyc-users-table tbody tr.kyc-row--risk-low    { border-left-color: #16a34a; }
.kyc-users-table tbody tr.kyc-row--risk-medium { border-left-color: #f59e0b; }
.kyc-users-table tbody tr.kyc-row--risk-high   { border-left-color: #dc2626; }

.kyc-cell-email { font-weight: 500; color: #0f172a; }

.kyc-risk-text--low    { color: #16a34a; font-weight: 600; }
.kyc-risk-text--medium { color: #d97706; font-weight: 600; }
.kyc-risk-text--high   { color: #dc2626; font-weight: 700; }

.kyc-status-pill {
  padding: 4px 10px 4px 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
}
.kyc-status-pill .kyc-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.85);
  display: inline-block;
}
.btn-round { border-radius: 8px !important; }

/* ── Classification cell + reclass indicator ── */
.kyc-cls-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.kyc-recls-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #FEF3C7;
  border: 1.5px solid #FCD34D;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #92400E;
  flex-shrink: 0;
}

/* ── Slide-out user profile panel (mockup parity) ──
   Panel sits below the global NAV (var(--nav-h)=56px), 580px wide,
   light overlay so the list stays visible behind it. */
.user-panel-backdrop {
  position: fixed;
  inset: var(--nav-h, 56px) 0 0 0;
  background: rgba(0, 0, 0, 0.18);
  z-index: 299;
  display: flex;
  justify-content: flex-end;
}
.user-side-panel {
  background: #fff;
  /* 620px gives the 4-cell status bar room for "Розничный (неквалиф.)"
     badges without wrapping mid-word, and leaves enough room for the
     floating close-button + the scrollbar. */
  width: 620px;
  max-width: 100vw;
  height: 100%;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.08);
  border-left: 1px solid var(--c-border, #EAECF0);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}
.user-side-panel-head { display: none; }
.user-side-panel-title { display: none; }
.user-side-panel-actions {
  position: absolute;
  top: 14px;
  right: 18px;
  z-index: 5;
  display: flex;
  gap: 6px;
}
.user-side-panel-btn {
  background: #fff;
  border: 1px solid var(--c-border, #EAECF0);
  border-radius: 8px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  color: var(--c-muted, #9CA3AF);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.user-side-panel-btn:hover { background: #F5F5F5; color: var(--c-text-2, #374151); }

/* Floating close-X — the accessibility shortcut that lives at the
   top-right corner of the panel. The big text "Закрыть" lives in
   the footer below. */
.user-side-panel-close-x {
  position: absolute;
  top: 14px;
  right: 18px;
  z-index: 5;
  width: 32px;
  height: 32px;
  background: #fff;
  border: 1px solid var(--c-border, #EAECF0);
  border-radius: 8px;
  cursor: pointer;
  color: var(--c-muted, #9CA3AF);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background-color .12s, color .12s;
}
.user-side-panel-close-x:hover { background: #F5F5F5; color: var(--c-text-2, #374151); }

.user-side-panel-body {
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  flex: 1 1 auto;
}

/* Footer with two CTAs — port of KYC_interactive .sp-footer. Sticky at
   the bottom of the side panel so the body scrolls under it. */
.user-side-panel-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  gap: 10px;
  padding: 12px 18px;
  background: #fff;
  border-top: 1px solid #EAECF0;
  flex-shrink: 0;
  z-index: 4;
}
.sp-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  flex: 1;
  border: 1px solid transparent;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease, transform .12s ease;
}
.sp-btn-ghost {
  flex: 0.7;
  background: #fff;
  border-color: #EAECF0;
  color: #374151;
}
.sp-btn-ghost:hover {
  background: #F9FAFB;
  border-color: #D1D5DB;
}
.sp-btn-primary {
  background: #16A37F;
  color: #fff;
  box-shadow: 0 1px 2px rgba(22, 163, 127, 0.25);
}
.sp-btn-primary:hover {
  background: #138C6C;
  box-shadow: 0 0 0 3px rgba(22, 163, 127, 0.18);
  transform: translateY(-1px);
}
.sp-btn-primary:active { transform: translateY(0); }
.user-panel-fade-enter-active,
.user-panel-fade-leave-active { transition: opacity .18s ease; }
.user-panel-fade-enter,
.user-panel-fade-leave-to { opacity: 0; }
.user-panel-slide-enter-active,
.user-panel-slide-leave-active { transition: transform .22s ease; }
.user-panel-slide-enter,
.user-panel-slide-leave-to { transform: translateX(100%); }
</style>

<style>
/* Docs modal — not scoped so modal portal gets styles */
.docs-modal .modal-content {
  border-radius: 16px;
  border: none;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}
.docs-modal-header {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: #fff;
  border-radius: 16px 16px 0 0;
  padding: 1rem 1.5rem;
  border: none;
}
.docs-modal-header .modal-title {
  font-weight: 600;
  font-size: 1.1rem;
}
.docs-modal-header .close {
  color: rgba(255, 255, 255, 0.9);
  opacity: 1;
  text-shadow: none;
}
.docs-modal-body {
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 0 0 16px 16px;
}
.docs-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #64748b;
}
.docs-error {
  border-radius: 10px;
  margin: 0;
}
.docs-list-wrap {
  max-height: 65vh;
  overflow-y: auto;
}
.docs-empty {
  text-align: center;
  padding: 2.5rem 1rem;
}
.docs-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.doc-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.doc-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
}
.doc-id {
  font-weight: 600;
  color: #475569;
  font-size: 0.9rem;
}
.doc-type {
  font-size: 0.8rem;
  color: #64748b;
  margin-left: 0.25rem;
}
.doc-status {
  margin-left: auto;
  font-size: 0.7rem;
}
.doc-card-body {
  padding: 1rem;
}
.doc-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.35rem;
  display: block;
}
.doc-status-select {
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #334155;
  background-color: #fff;
  margin-bottom: 1rem;
  min-height: 38px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.doc-status-select:hover {
  border-color: #0ea5e9;
}
.doc-status-select:focus {
  border-color: #0284c7;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.2);
  outline: none;
}
.doc-textarea {
  font-size: 0.85rem;
  border-radius: 8px;
  margin-bottom: 0.75rem;
}
.doc-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}
.doc-btn {
  border-radius: 8px;
}
.doc-btn-update {
  font-size: 0.8rem;
}
</style>
